import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { CreatePatientTaskRequestSchema, type CreatePatientTaskRequest } from '@ctms/contracts';
import { z } from 'zod';
import { AlertCircle, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { PersonnelSelect } from '../../trials/personnel-select/personnel-select.js';
import { visitsApi } from '../visits.api.js';

/**
 * The description is the only required text (REQ-VIS-051: a task without a
 * description does not save); the comment is optional. The person travels as a
 * string so the empty placeholder stays valid until one is picked — the dialog
 * converts it to a number before the request (OPCJA 1, ADR-006).
 */
const AddTaskFormSchema = z.object({
  name: z.string().trim().min(1, 'Opis zadania jest wymagany.').max(255),
  comments: z.string().max(2000, 'Komentarz może mieć najwyżej 2000 znaków.').optional(),
  startPersonnelId: z.string().min(1, 'Wybierz osobę.'),
});

type AddTaskFormInput = z.infer<typeof AddTaskFormSchema>;

export interface AddTaskDialogProps {
  /** The Domain 3 participation the task hangs on (REQ-VIS-051). */
  enrollmentId: number;
  /** Patient name for the header (presentation only). */
  patientName: string | null;
  /** Called after the server stored the task — the caller invalidates the list. */
  onSaved: () => void;
  onClose: () => void;
}

const fieldClass =
  'mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100';
const labelClass = 'text-xs text-slate-400';

/**
 * "Nowe zadanie" — opens one to-do task on the participation (REQ-VIS-051,
 * `#add-task`). The creation date is never asked for: the server stamps its
 * today and returns the row as stored.
 */
export function AddTaskDialog({
  enrollmentId,
  patientName,
  onSaved,
  onClose,
}: AddTaskDialogProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AddTaskFormInput>({
    resolver: zodResolver(AddTaskFormSchema),
    defaultValues: { name: '', comments: '', startPersonnelId: '' },
  });

  const saveMutation = useMutation({
    mutationFn: (request: CreatePatientTaskRequest) => visitsApi.createPatientTask(request),
    onSuccess: () => {
      onSaved();
    },
    onError: async (err: unknown) => {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się zapisać zadania.'));
    },
  });

  const isSubmitting = saveMutation.isPending;

  const onSubmit = (input: AddTaskFormInput): void => {
    setErrorMessage(null);
    const comments = input.comments?.trim() ?? '';
    const request: CreatePatientTaskRequest = {
      enrollmentId,
      name: input.name.trim(),
      comments: comments.length > 0 ? comments : null,
      startPersonnelId: Number(input.startPersonnelId),
    };
    const parsed = CreatePatientTaskRequestSchema.safeParse(request);
    if (!parsed.success) {
      setErrorMessage('Nie udało się zapisać zadania.');
      return;
    }
    saveMutation.mutate(parsed.data);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-task-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <h2 id="add-task-title" className="text-base font-semibold text-slate-100">
            Nowe zadanie{patientName ? ` — ${patientName}` : ''}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Zamknij"
            className="rounded-md p-1.5 text-slate-400 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          onSubmit={(event) => {
            void handleSubmit(onSubmit)(event);
          }}
          className="mt-5 space-y-4"
          noValidate
        >
          {errorMessage !== null && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-xs text-rose-300"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <label htmlFor="new-task-name" className={labelClass}>
              Opis zadania
            </label>
            <input id="new-task-name" type="text" disabled={isSubmitting} className={fieldClass} {...register('name')} />
            {errors.name !== undefined && <p className="mt-1 text-xs text-rose-400">{errors.name.message}</p>}
          </div>

          <div>
            <label htmlFor="new-task-comments" className={labelClass}>
              Komentarz (opcjonalnie)
            </label>
            <textarea
              id="new-task-comments"
              disabled={isSubmitting}
              className={`${fieldClass} min-h-20`}
              {...register('comments')}
            />
            {errors.comments !== undefined && (
              <p className="mt-1 text-xs text-rose-400">{errors.comments.message}</p>
            )}
          </div>

          <div>
            <PersonnelSelect id="new-task-person" registration={register('startPersonnelId')} disabled={isSubmitting} />
            {errors.startPersonnelId !== undefined && (
              <p className="mt-1 text-xs text-rose-400">{errors.startPersonnelId.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            <button
              type="button"
              id="cancel-task"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
            >
              Anuluj
            </button>
            <button
              type="submit"
              id="save-task"
              disabled={isSubmitting}
              className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
            >
              {isSubmitting ? 'Zapisywanie…' : 'Zapisz zadanie'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}